import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldInput } from "@/components/FieldInput";
import { api, can, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";

const CFG = {
  dse: { label: "DSE", id: "dse_id", name: "dse_name", opening: "opening_outstanding", openingLabel: "Opening Outstanding (₹)" },
  fl: { label: "FL", id: "fl_id", name: "fl_name", opening: "opening_advance", openingLabel: "Opening Advance (₹)" },
  retailer: { label: "Retailer", id: "retailer_id", name: "retailer_name", opening: "opening_due", openingLabel: "Opening VTOP Due (₹)" },
};

function MasterDialog({ kind, item, onClose, onSaved }) {
  const c = CFG[kind];
  const { user } = useAuth();
  const [v, setV] = useState({});
  useEffect(() => {
    setV(item ? { code: item[c.id], name: item[c.name], mobile: item.mobile, location: item.location, opening: String(item[c.opening] ?? 0), status: item.status, default_rate: String(item.default_rate ?? "") } : { status: "Active", opening: "0" });
  }, [item, c]);
  const isAdmin = user.role === "admin";
  const fields = [
    { name: "code", label: `${c.label} ID (blank = auto)`, type: "text" },
    { name: "name", label: `${c.label} Name`, type: "text", required: true },
    { name: "mobile", label: "Mobile", type: "text" },
    { name: "location", label: "Location / Area", type: "text" },
    { name: "opening", label: c.openingLabel, type: "money", hint: isAdmin ? "Start date par pending amount" : "Sirf Admin opening balance set/change kar sakta hai" },
    { name: "status", label: "Status", type: "select", options: ["Active", "Inactive"], required: true },
    ...(kind === "fl" ? [{ name: "default_rate", label: "Default commission rate (₹/unit)", type: "money" }] : []),
  ].filter((f) => !(item && f.name === "code"));
  const save = async () => {
    if (!v.name) return toast.error("Name is required");
    try {
      if (item) await api.put(`/masters/${kind}/${item[c.id]}`, v);
      else await api.post(`/masters/${kind}`, v);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`master-${kind}-dialog`}>
        <DialogHeader><DialogTitle className="font-display">{item ? `Edit ${c.label} — ${item[c.id]}` : `Add ${c.label}`}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f.name} className={f.hint ? "sm:col-span-2" : ""}>
              <FieldInput f={f} value={v[f.name]} onChange={(x) => setV((s) => ({ ...s, [f.name]: x }))} masters={{ dse: [], fl: [], schemes: [] }} testPrefix={`master-${kind}`} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} data-testid={`master-${kind}-save`}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MasterTable({ kind, rows, reload }) {
  const c = CFG[kind];
  const { user } = useAuth();
  const [edit, setEdit] = useState(undefined);
  const w = can(user, "master:write");
  return (
    <div className="space-y-3">
      {w && <Button size="sm" onClick={() => setEdit(null)} data-testid={`add-${kind}-btn`}><Plus className="h-4 w-4" /> Add {c.label}</Button>}
      <div className="overflow-auto border border-slate-200 rounded-xl bg-white">
        <table className="dense-table" data-testid={`${kind}-master-table`}>
          <thead><tr><th>ID</th><th>Name</th><th>Mobile</th><th>Location</th><th className="text-right">{c.openingLabel}</th>{kind === "fl" && <th className="text-right">Rate</th>}<th>Status</th>{w && <th />}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No {c.label} added yet</td></tr>}
            {rows.map((r) => (
              <tr key={r[c.id]} data-testid={`${kind}-row-${r[c.id]}`}>
                <td className="num font-semibold">{r[c.id]}</td><td>{r[c.name]}</td><td className="num">{r.mobile}</td><td>{r.location}</td>
                <td className="num text-right">{inr(r[c.opening])}</td>
                {kind === "fl" && <td className="num text-right">{inr(r.default_rate)}</td>}
                <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${r.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}`}>{r.status}</span></td>
                {w && <td><button onClick={() => setEdit(r)} data-testid={`edit-${kind}-${r[c.id]}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit !== undefined && <MasterDialog kind={kind} item={edit} onClose={() => setEdit(undefined)} onSaved={reload} />}
    </div>
  );
}

function Schemes({ schemes, reload }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => setList(schemes.map((s) => ({ ...s, rate: String(s.rate) }))), [schemes]);
  const admin = user.role === "admin";
  const save = async () => {
    try {
      await api.put("/settings/schemes", list.filter((s) => s.name));
      toast.success("Schemes saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3 max-w-xl">
      <span className="hint-tag">Commission rate aur scheme aapke hisaab se configurable — Gross = Units × Rate</span>
      {list.map((s, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Scheme name (e.g. MNP)" value={s.name} disabled={!admin} onChange={(e) => setList((l) => l.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`scheme-name-${i}`} />
          <Input placeholder="Rate ₹" type="number" className="w-32 num" value={s.rate} disabled={!admin} onChange={(e) => setList((l) => l.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))} data-testid={`scheme-rate-${i}`} />
          {admin && <Button variant="ghost" size="icon" onClick={() => setList((l) => l.filter((_, j) => j !== i))} data-testid={`scheme-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
      {list.length === 0 && <p className="text-sm text-slate-400">No schemes yet.</p>}
      {admin && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setList((l) => [...l, { name: "", rate: "" }])} data-testid="scheme-add-btn"><Plus className="h-4 w-4" /> Add scheme</Button>
          <Button size="sm" onClick={save} data-testid="scheme-save-btn">Save schemes</Button>
        </div>
      )}
    </div>
  );
}

function DistRow({ d, admin, reload }) {
  const [v, setV] = useState({ name: d.name, capital_invested: String(d.capital_invested), status: d.status });
  const save = async () => {
    try {
      await api.put(`/distributors/${d.code}`, v);
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <tr data-testid={`distributor-row-${d.code}`}>
      <td className="num font-semibold">{d.code}</td>
      <td><Input value={v.name} disabled={!admin} onChange={(e) => setV((s) => ({ ...s, name: e.target.value }))} className="h-8" /></td>
      <td><Input type="number" value={v.capital_invested} disabled={!admin} onChange={(e) => setV((s) => ({ ...s, capital_invested: e.target.value }))} className="h-8 num w-36" data-testid={`distributor-capital-${d.code}`} /></td>
      <td>{d.status}</td>
      <td>{admin && <Button size="sm" className="h-8" onClick={save} data-testid={`distributor-save-${d.code}`}>Save</Button>}</td>
    </tr>
  );
}

function Distributors({ rows, reload }) {
  const { user } = useAuth();
  return (
    <div className="space-y-3 max-w-3xl">
      <span className="hint-tag">3 alag VI distributor accounts — har entry kisi ek distributor se judti hai. Opening VI balance = VI ledger mein Opening entry.</span>
      <div className="overflow-auto border border-slate-200 rounded-xl bg-white">
        <table className="dense-table"><thead><tr><th>Code</th><th>Name</th><th>Capital Invested (₹)</th><th>Status</th><th /></tr></thead>
          <tbody>{rows.map((d) => <DistRow key={d.code} d={d} admin={user.role === "admin"} reload={reload} />)}</tbody>
        </table>
      </div>
    </div>
  );
}

function Rates({ rates, reload }) {
  const { user } = useAuth();
  const [v, setV] = useState({});
  useEffect(() => setV({ vi: String(rates.vi ?? ""), dse: String(rates.dse ?? ""), rtl: String(rates.retailer ?? "") }), [rates.vi, rates.dse, rates.retailer]);
  const admin = user.role === "admin";
  const save = async () => {
    try {
      await api.put("/settings/rates", { vi_extra_rate: v.vi, dse_extra_rate: v.dse, retailer_margin_rate: v.rtl });
      toast.success("Rates saved — nayi entries par lagenge, purani entries apna rate rakhti hain");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ex = 50000;
  const vx = (ex * Number(v.vi || 0)) / 100;
  const dx = (ex * Number(v.dse || 0)) / 100;
  return (
    <div className="space-y-4 max-w-xl">
      <span className="hint-tag">Har transaction par us waqt ka rate save hota hai — rate badalne se purane records nahi badlenge</span>
      <div className="grid grid-cols-2 gap-3">
        <div><div className="text-xs text-slate-600 mb-1">VI Extra Rate (%)</div>
          <Input type="number" step="0.01" className="num" value={v.vi || ""} disabled={!admin} onChange={(e) => setV((s) => ({ ...s, vi: e.target.value }))} data-testid="rate-vi-input" /></div>
        <div><div className="text-xs text-slate-600 mb-1">DSE Extra Rate (%)</div>
          <Input type="number" step="0.01" className="num" value={v.dse || ""} disabled={!admin} onChange={(e) => setV((s) => ({ ...s, dse: e.target.value }))} data-testid="rate-dse-input" /></div>
        <div><div className="text-xs text-slate-600 mb-1">Retailer VTOP Margin Rate (%)</div>
          <Input type="number" step="0.01" className="num" value={v.rtl || ""} disabled={!admin} onChange={(e) => setV((s) => ({ ...s, rtl: e.target.value }))} data-testid="rate-retailer-input" /></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm grid grid-cols-2 gap-1.5 num" data-testid="rate-example">
        <span>Example base</span><b className="text-right">{inr(ex)}</b>
        <span>VI virtual credit</span><b className="text-right">{inr(ex + vx)}</b>
        <span>DSE total given</span><b className="text-right">{inr(ex + dx)}</b>
        <span>Gross profit</span><b className="text-right text-emerald-700">{inr(vx - dx)}</b>
      </div>
      {admin && <Button size="sm" onClick={save} data-testid="rates-save-btn">Save rates</Button>}
    </div>
  );
}

export default function Masters() {
  const m = useMasters();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Masters</h1>
        <span className="hint-tag mt-2">DSE aur FL ki details, opening balances aur commission schemes</span>
      </div>
      <Tabs defaultValue="dse">
        <TabsList>
          <TabsTrigger value="dse" data-testid="masters-tab-dse">DSE Master</TabsTrigger>
          <TabsTrigger value="fl" data-testid="masters-tab-fl">FL Master</TabsTrigger>
          <TabsTrigger value="retailer" data-testid="masters-tab-retailer">Retailer Master</TabsTrigger>
          <TabsTrigger value="schemes" data-testid="masters-tab-schemes">Commission Schemes</TabsTrigger>
          <TabsTrigger value="rates" data-testid="masters-tab-rates">VI / DSE Extra Rates</TabsTrigger>
          <TabsTrigger value="distributors" data-testid="masters-tab-distributors">Distributors</TabsTrigger>
        </TabsList>
        <TabsContent value="dse" className="mt-4"><MasterTable kind="dse" rows={m.dse} reload={m.reload} /></TabsContent>
        <TabsContent value="fl" className="mt-4"><MasterTable kind="fl" rows={m.fl} reload={m.reload} /></TabsContent>
        <TabsContent value="retailer" className="mt-4"><MasterTable kind="retailer" rows={m.retailer} reload={m.reload} /></TabsContent>
        <TabsContent value="schemes" className="mt-4"><Schemes schemes={m.schemes} reload={m.reload} /></TabsContent>
        <TabsContent value="rates" className="mt-4"><Rates rates={m.rates} reload={m.reload} /></TabsContent>
        <TabsContent value="distributors" className="mt-4"><Distributors rows={m.distributors} reload={m.reload} /></TabsContent>
      </Tabs>
    </div>
  );
}
